import * as React from "react";

import { RobotoLLMContext } from "@/shared/domain/chat/ChatRecord.ts";
import { useViewingContext, ViewingContext } from "@/shared/services";

import { ContextType } from "../contextType";

/** Maps IDs to human-readable display names for the context widget */
export interface ContextDisplayNames {
  datasets: Record<string, string>;
  files: Record<string, string>;
}

interface DerivedContext {
  llmContext: RobotoLLMContext;
  displayNames: ContextDisplayNames;
}

const viewingContextToDerivedContext = (
  viewingContext: ViewingContext,
): DerivedContext => {
  const datasetIds: string[] = [];
  const fileIds: string[] = [];
  const datasetDisplayNames: Record<string, string> = {};
  const fileDisplayNames: Record<string, string> = {};

  // Dataset from dataset page context
  if (viewingContext.dataset?.datasetId) {
    const id = viewingContext.dataset.datasetId;
    datasetIds.push(id);
    const record = viewingContext.dataset.record;
    if (record?.name) {
      datasetDisplayNames[id] = record.name;
    }

    if (
      viewingContext.dataset.selectedFiles &&
      viewingContext.dataset.selectedFiles.length > 0
    ) {
      viewingContext.dataset.selectedFiles.forEach((file) => {
        fileIds.push(file.file_id);
        fileDisplayNames[file.file_id] = file.relative_path;
      });
    }
  }

  // Datasets and files from visualizer context
  for (const source of viewingContext.visualizer?.dataSources ?? []) {
    if (source.type === "dataset") {
      datasetIds.push(source.id);
      if (source.record?.name) {
        datasetDisplayNames[source.id] = source.record.name;
      }
    } else if (source.type === "file") {
      fileIds.push(source.id);
      if (source.record?.relative_path) {
        fileDisplayNames[source.id] = source.record.relative_path;
      }
    }
  }

  if (viewingContext.file?.fileId) {
    fileIds.push(viewingContext.file.fileId);
    if (viewingContext.file.record?.relative_path) {
      fileDisplayNames[viewingContext.file.fileId] =
        viewingContext.file.record.relative_path;
    }
  }

  return {
    llmContext: {
      dataset_ids: datasetIds,
      file_ids: fileIds,
      visualizer_state: viewingContext.visualizer?.state,
      misc_context: undefined,
    },
    displayNames: {
      datasets: datasetDisplayNames,
      files: fileDisplayNames,
    },
  };
};

export const useRobotoLLMContext = (): {
  context: RobotoLLMContext;
  displayNames: ContextDisplayNames;
  handleContextDelete: (
    contextType: ContextType,
    contextContent: string,
  ) => void;
} => {
  const viewingContext = useViewingContext();

  const [derivedContext, setDerivedContext] = React.useState(() =>
    viewingContextToDerivedContext(viewingContext),
  );

  React.useEffect(() => {
    setDerivedContext(viewingContextToDerivedContext(viewingContext));
  }, [viewingContext]);

  const handleContextDelete = (
    contextType: ContextType,
    contextContent: string,
  ) => {
    setDerivedContext((prev) => {
      if (contextType === "dataset") {
        return {
          ...prev,
          llmContext: {
            ...prev.llmContext,
            dataset_ids: prev.llmContext.dataset_ids.filter(
              (id) => id !== contextContent,
            ),
          },
        };
      } else if (contextType === "file") {
        return {
          ...prev,
          llmContext: {
            ...prev.llmContext,
            file_ids: prev.llmContext.file_ids.filter(
              (id) => id !== contextContent,
            ),
          },
        };
      } else if (contextType === "viz_state") {
        return {
          ...prev,
          llmContext: {
            ...prev.llmContext,
            visualizer_state: undefined,
          },
        };
      }
      return prev;
    });
  };

  return {
    context: derivedContext.llmContext,
    displayNames: derivedContext.displayNames,
    handleContextDelete,
  };
};
